import { uiMessage } from '../utils/uiTranslation';
import { uiText, useUiLanguage } from '../utils/uiTranslation';
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

const CalendarCallback = () => {
  useUiLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing calendar connection...');
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate calls in React StrictMode (React 18 development mode issue)
    if (!hasProcessedRef.current) {
      hasProcessedRef.current = true;
      console.log('🔄 CalendarCallback: Starting OAuth processing...');
      handleCallback();
    } else {
      console.log('⚠️ CalendarCallback: Duplicate call prevented!');
    }
  }, []);

  const handleCallback = async () => {
    try {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      console.log('📍 CalendarCallback: Processing callback with:', {
        code: code ? `${code.substring(0, 10)}...` : null,
        state,
        error
      });

      if (error) {
        console.error('❌ OAuth error:', error);
        setStatus('error');
        setMessage(uiMessage('Authorization failed. Please try connecting again.'));
        return;
      }

      if (!code || !state) {
        console.error('❌ Missing parameters:', { code: !!code, state: !!state });
        setStatus('error');
        setMessage('Missing authorization code or state parameter');
        return;
      }

      // Parse provider and user ID from state
      const [provider, userId] = state.split('_');

      if (!provider || !userId) {
        console.error('❌ Invalid state parameter:', state);
        setStatus('error');
        setMessage('Invalid state parameter');
        return;
      }

      console.log(`🔗 Connecting to ${provider} for user ${userId}`);
      setMessage(uiMessage('Connecting to {{provider}}...', { provider }));

      // Exchange code for tokens
      console.log('🚀 Making API call to exchange code for tokens...');
      const response = await fetch(`${BACKEND_URL}/calendar/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: provider,
          user_id: userId,
          code: code,
          state: state
        })
      });

      console.log('📡 API response status:', response.status);
      const data = await response.json();
      console.log('📦 API response data:', { success: data.success, error: data.error });

      if (data.success) {
        setStatus('success');
        setMessage(uiMessage('Successfully connected to {{provider}}!', { provider }));

        // Send success message to parent window
        if (window.opener) {
          window.opener.postMessage({
            type: 'CALENDAR_AUTH_SUCCESS',
            provider: provider,
            credentials: data  // Pass the complete data object from backend
          }, window.location.origin);
        }

        // Close the popup after a short delay
        setTimeout(() => {
          window.close();
        }, 2000);

      } else {
        setStatus('error');
        setMessage(uiMessage('Connection failed. Please try again.'));

        // Send error message to parent window
        if (window.opener) {
          window.opener.postMessage({
            type: 'CALENDAR_AUTH_ERROR',
            error: data.error || 'Unknown error'
          }, window.location.origin);
        }
      }

    } catch (error) {
      console.error('Calendar callback error:', error);
      setStatus('error');
      setMessage(uiMessage('Connection failed. Please try again.'));

      // Send error message to parent window
      if (window.opener) {
        window.opener.postMessage({
          type: 'CALENDAR_AUTH_ERROR',
          error: error.message
        }, window.location.origin);
      }
    }
  };

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate('/profile');
    }
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#f8f9fa',
      fontFamily: 'Montserrat, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        maxWidth: '400px',
        width: '90%'
      }}>
        <div style={{ marginBottom: '20px' }}>
          {status === 'processing' && (
            <i className="fa-solid fa-spinner fa-spin" style={{
              fontSize: '48px',
              color: '#568ded',
              marginBottom: '20px'
            }}></i>
          )}

          {status === 'success' && (
            <i className="fa-solid fa-check-circle" style={{
              fontSize: '48px',
              color: 'var(--app-tone-28a745, #28a745)',
              marginBottom: '20px'
            }}></i>
          )}

          {status === 'error' && (
            <i className="fa-solid fa-exclamation-triangle" style={{
              fontSize: '48px',
              color: 'var(--app-tone-dc3545, #dc3545)',
              marginBottom: '20px'
            }}></i>
          )}
        </div>

        <h2 style={{
          color: "var(--app-neutral-ink, #333)",
          marginBottom: '15px',
          fontSize: '24px'
        }}>
          {status === 'processing' && uiText("Connecting Calendar")}
          {status === 'success' && uiText("Connection Successful!")}
          {status === 'error' && uiText("Connection Failed")}
        </h2>

        <p style={{
          color: '#666',
          marginBottom: '30px',
          fontSize: '16px',
          lineHeight: '1.5'
        }}>
          {uiText(message)}
        </p>

        {status !== 'processing' && (
          <button
            onClick={handleClose}
            style={{ '--light-control-fill': '#568ded',
              backgroundColor: '#568ded',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '12px 24px',
              fontSize: '16px',
              cursor: 'pointer',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: "var(--app-weight-strong, bold)"
            }}
            data-light-control={"filled"}>
            {window.opener ? uiText("Close Window") : uiText("Return to Profile")}
          </button>
        )}

        {status === 'success' && (
          <p style={{
            marginTop: '20px',
            fontSize: '14px',
            color: '#666',
            fontStyle: 'italic'
          }}>{uiText("This window will close automatically in a few seconds...")}</p>
        )}
      </div>
    </div>
  );
};

export default CalendarCallback; 